import React, { useMemo, useCallback } from 'react';
import { Switch, FormControlLabel, Box, Checkbox } from '@mui/material';

import { Site, SiteTableProps, SaleStaff } from '../../types/site.types';

import BaseDataTable from '@/components/table/base-data-table';
import { ColumnConfig } from '@/components/types/types';
import { convertDateTimeFormat } from '@/utils/date-time';
import { getCellStyle } from '@/components/styles/table-styles';

interface EnhancedSiteTableProps extends SiteTableProps {
  sortField?: string | null;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (field: string, order: 'asc' | 'desc') => void;
  isMobile?: boolean;
}
const SiteTable: React.FC<EnhancedSiteTableProps> = ({
  onSiteClick,
  data = [],
  loading = false,
  currentPage = 1,
  itemsPerPage = 100,
  totalItems = 0,
  selectedSites = [],
  onPageChange,
  onItemsPerPageChange,
  onSelectionChange: _onSelectionChange,
  onArchive,
  onRestore,
  sortField = 'updatedAt',
  sortOrder = 'desc',
  onSortChange,
  isMobile = false,
}) => {
  // Handle individual row actions
  const handleRowArchive = useCallback(
    (siteId: string) => {
      onArchive?.([siteId]);
    },
    [onArchive]
  );

  const handleRowRestore = useCallback(
    (siteId: string) => {
      onRestore?.([siteId]);
    },
    [onRestore]
  );

  // Use data as-is since BaseDataTable will handle sorting
  const tableData = useMemo(() => data || [], [data]);
  const dataCanUpdate = useMemo(() => tableData.filter((site) => !site.readOnly), [tableData]);
  const visibleIds = useMemo(() => dataCanUpdate.map((site) => site.id), [dataCanUpdate]);

  const selectedOnPageCount = useMemo(
    () => visibleIds.filter((id) => selectedSites.includes(id)).length,
    [visibleIds, selectedSites]
  );

  // Check if all rows on current page are selected
  const allSelected = useMemo(
    () => dataCanUpdate.length > 0 && selectedOnPageCount === dataCanUpdate.length,
    [dataCanUpdate, selectedOnPageCount]
  );

  // Check if some (but not all) rows on current page are selected
  const someSelected = useMemo(
    () => selectedOnPageCount > 0 && selectedOnPageCount < dataCanUpdate.length,
    [selectedOnPageCount, dataCanUpdate]
  );

  // Handle select all checkbox - only select items on current page
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      // If in indeterminate state (some selected), always deselect all on current page
      if (someSelected) {
        const newSelections = selectedSites.filter((id) => !visibleIds.includes(id));
        _onSelectionChange?.(newSelections);
        return;
      }

      if (checked) {
        // Select all items visible on current page
        // Merge with existing selections from other pages
        const newSelections = [...selectedSites];
        visibleIds.forEach((id) => {
          if (!newSelections.includes(id)) {
            newSelections.push(id);
          }
        });
        _onSelectionChange?.(newSelections);
      } else {
        // Deselect only current page items
        const newSelections = selectedSites.filter((id) => !visibleIds.includes(id));
        _onSelectionChange?.(newSelections);
      }
    },
    [someSelected, selectedSites, visibleIds, _onSelectionChange]
  );

  // Handle individual checkbox change
  const handleCheckboxChange = useCallback(
    (site: Site, checked: boolean) => {
      if (checked) {
        if (!selectedSites.includes(site.id)) {
          _onSelectionChange?.([...selectedSites, site.id]);
        }
        return;
      } else {
        _onSelectionChange?.(selectedSites.filter((id) => id !== site.id));
      }
    },
    [selectedSites, _onSelectionChange]
  );

  // Define column configuration for the site table
  const columns: ColumnConfig<Site>[] = useMemo(() => {
    const allColumns: ColumnConfig<Site>[] = [
      {
        key: 'checkbox',
        label: '',
        sortable: false,
        width: { xs: '5%', sm: '5%', md: '5%' },
        renderHeader: () => (
          <Box onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
          </Box>
        ),
        render: (_, site: Site) => (
          <Box onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selectedSites.includes(site.id)}
              disabled={site.readOnly}
              readOnly={site.readOnly}
              onChange={(e) => {
                e.stopPropagation();
                handleCheckboxChange(site, e.target.checked);
              }}
            />
          </Box>
        ),
      },
      {
        key: 'propertyAbbreviation',
        label: '現場略称',
        sortable: false,
        width: { xs: '20%', sm: '20%', md: '20%' },
        render: (value: string) => <Box sx={getCellStyle('100%')}>{value}</Box>,
        useInMobile: true,
      },
      {
        key: 'constructionCompany',
        label: '建設会社',
        sortable: false,
        width: { xs: '25%', sm: '25%', md: '25%' },
        render: (value: string) => <Box sx={getCellStyle('150px')}>{value}</Box>,
        useInMobile: true,
      },
      {
        key: 'salesRepresentative',
        label: '営業',
        sortable: false,
        width: { xs: '25%', sm: '25%', md: '25%' },
        render: (value: SaleStaff) => (
          <Box sx={getCellStyle('100%')} title={value ? `${value.lastName}${value.firstName}` : ''}>
            {value ? `${value.lastName}${value.firstName}` : ''}
          </Box>
        ),
      },
      {
        key: 'updatedAt',
        label: '更新日時',
        sortable: true,
        sortField: 'updatedAt',
        width: { xs: '15%', sm: '15%', md: '15%' },
        render: (value: string) => (
          <Box sx={getCellStyle('100%')}>{convertDateTimeFormat(value)}</Box>
        ),
      },
      {
        key: 'isArchived',
        label: 'アーカイブ',
        sortable: false,
        width: { xs: '10%', sm: '10%', md: '10%' },
        render: (value: boolean, site: Site) => (
          <Box onClick={(e) => e.stopPropagation()}>
            <FormControlLabel
              control={
                <Switch
                  checked={value}
                  disabled={site.readOnly}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (value) {
                      handleRowRestore(site.id);
                    } else {
                      handleRowArchive(site.id);
                    }
                  }}
                  color='primary'
                />
              }
              label=''
              sx={{ margin: 0 }}
            />
          </Box>
        ),
      },
    ];
    // If mobile, filter to only columns marked for mobile use
    return isMobile ? allColumns.filter((col) => col.useInMobile) : allColumns;
  }, [
    selectedSites,
    handleCheckboxChange,
    handleRowRestore,
    handleRowArchive,
    handleSelectAll,
    allSelected,
    someSelected,
    isMobile,
  ]);

  // Custom row styling for site table
  const getRowStyle = useCallback(
    (site: Site) => {
      // Priority: Selected rows > Archived rows
      if (selectedSites.includes(site.id)) {
        return {
          backgroundColor: '#D7E2FF', // Primary90 - light primary background (selected)
        };
      }
      // Highlight archived rows with gray - hover will make it darker (0.5 -> 0.8)
      if (site.isArchived) {
        return {
          backgroundColor: 'rgba(208, 213, 221, 0.5)', // Gray for archived items
        };
      }
      return {};
    },
    [selectedSites]
  );

  return (
    <BaseDataTable<Site>
      data={tableData}
      loading={loading}
      onRowClick={onSiteClick}
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onItemsPerPageChange={onItemsPerPageChange}
      sortField={sortField}
      sortOrder={sortOrder}
      onSortChange={onSortChange}
      columns={columns}
      enableServerSort={true} // Enable server-side sorting like other tables
      hidePagination={true}
      getRowStyle={getRowStyle}
      wrapperStyles={{}}
    />
  );
};

export default SiteTable;
